import AsyncSelect from '@/components/form-elements/AsyncSelect/AsyncSelect.vue';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import {
  ComputedOrBoolFunction,
  FieldConfig,
  INFO_ICON_TYPE,
  RenderingContext,
} from '@/utils/form';
import Datepicker from '@/components/form-elements/Datepicker/Datepicker.vue';
import { birthdayConfig } from '@/components/form-elements/Datepicker/configs';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { isKradSparte } from '@/features/Sparten/shared/kfz/isKradSparte';
import {
  DsButton,
  DsCheckbox,
  DsFormattedNumberInput,
  DsSwitch,
} from '@demvsystems/design-components';
import { FORM_LAYOUT } from '@/utils/form/formLayout';
import { computed } from 'vue';
import { useInputStore } from '@/features/Sparten';
import FahrerkreisTypGroup from '@/features/Sparten/shared/components/FahrerkreisTypGroup.vue';
import { FAHRERKREIS_TYP } from '@/features/Sparten/shared/types';
import {
  getDriverWithHauptnutzerFlag,
  nextAvailableFahrerRole,
  usedFahrerRoles,
} from '@/features/Sparten/shared/components/kfz/Fahrerkreis/utils';
import { WKZ } from '@/features/Sparten/shared/types/kfz/types';
import { ageFormattedNumberInputAttrs } from '@/features/Sparten/shared/kfz/formattedNumberInputConfigs';
import { OPTIONAL_ROLE, Role, useClientStore } from '@/features/Clients';
import {
  KfzFahrerResource,
  kfzFahrerSchema,
} from '@/features/Sparten/shared/schema/kfz/clientSchema/clientSchema';
import { useGlobalStore } from '@/features/Global/store';
import { isKfzV2Sparte } from '@/features/Sparten/shared/kfz/kfzV2Switch';

export const isEinzelnutzer: ComputedOrBoolFunction<KfzFahrerResource> = (
  context,
): boolean => {
  if (context.client !== undefined) {
    return getSimpleChoiceMeta(
      SIMPLE_CHOICE.BEZUG_ZUM_VN_JURISTISCHE_PERSON,
      SIMPLE_CHOICE_META.BEZUG_ZUM_VN_IS_EINZELNUTZER,
    ).includes(context.client.meta.bezugZumVn);
  }

  return true;
};

/**
 * Wie `isEinzelnutzer`, blendet das Feld in den V2-Fake-Sparten (campv2/pkwv2) aber
 * aus: Dort ist das per-Fahrer-`hasAnSicherheitstrainingTeilgenommen` entfallen
 * (ersetzt durch das VN-Datumsfeld `anSicherheitstrainingTeilnahmedatum`) und wird von
 * der clientDehydration verworfen — die Checkbox wäre sonst ein wirkungsloses
 * Bedienelement. V1 (pkw/camp/krad/nutz) bleibt unverändert.
 */
const isEinzelnutzerAndNotKfzV2Sparte = (
  context: RenderingContext<KfzFahrerResource>,
): boolean =>
  isEinzelnutzer(context) && !isKfzV2Sparte(useGlobalStore().sparte);

const isEinzelnutzerAndVorsteuerabzugsBerechtigt = (
  context: RenderingContext<KfzFahrerResource>,
): boolean => {
  if (context.client !== undefined) {
    return (
      isEinzelnutzer(context) &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.KFZ_BERUFSSTATUS,
        SIMPLE_CHOICE_META.KFZ_BERUFSSTATUS_IS_VORSTEUER_ABZUGS_BERECHTIGT,
      ).includes(context.client.meta.berufsstatus)
    );
  }

  return false;
};

const hasEinzelnutzerMehrereKinder = (
  context: RenderingContext<KfzFahrerResource>,
): boolean => {
  if (context.client !== undefined) {
    return (
      isEinzelnutzer(context) &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.MINOR_CHILDREN,
        SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
      ).includes(context.client.meta.hasMinderjaehrigeKinder)
    );
  }

  return true;
};

const hasEinzelnutzerJahreskarteOeffentlicherPersonenverkehr = (
  context: RenderingContext<KfzFahrerResource>,
): boolean => {
  if (context.client !== undefined) {
    return (
      isEinzelnutzer(context) &&
      context.client.meta.hasJahreskarteOeffentlicherPersonenverkehr
    );
  }

  return true;
};

const hasEinzelnutzerWohneigentum = (
  context: RenderingContext<KfzFahrerResource>,
): boolean => {
  if (context.client !== undefined) {
    return isEinzelnutzer(context) && context.client.meta.hasWohneigentum;
  }

  return true;
};

const isNotCompanyAndOnlyVn = computed(
  () =>
    useInputStore().versicherungsnehmer.isFirma === false &&
    useInputStore().fahrerkreis.typ !== FAHRERKREIS_TYP.NUR_VN,
);

export const moreThanOneNutzer = computed(
  () => usedFahrerRoles.value.length > 1,
);

export const bezugZumVnJuristischePerson: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.bezugZumVn',
  label: 'Bezug zum Versicherungsnehmer',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.BEZUG_ZUM_VN_JURISTISCHE_PERSON,
  },
  clientField: true,
};

const hasAgeRestriction = computed(
  () =>
    useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.OFFEN &&
    useInputStore().fahrerkreis.altersbeschraenkung,
);

export const bezugZumVnPrivatePerson: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.bezugZumVn',
  label: 'Bezug zum Versicherungsnehmer',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.BEZUG_ZUM_VN_PRIVAT_PERSON,
  },
  clientField: true,
};

export const anredeId: FieldConfig = {
  component: AsyncSelect,
  path: 'anredeId',
  label: 'Anrede',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.SALUTATION,
  },
  clientField: true,
  skipLiveValidationWhenEqual: ['4'],
};

export const geburtsdatum: FieldConfig = {
  component: Datepicker,
  path: 'geburtsdatum',
  label: 'Geburtsdatum',
  inputAttrs: {
    datepickerConfig: birthdayConfig,
  },
  clientField: true,
  isRequired: true,
};

export const familienstand: FieldConfig = {
  component: AsyncSelect,
  path: 'familienstand',
  label: 'Familienstand',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.PERSONAL_STATUS,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
  isRequired: true,
};

export const nationalitaet: FieldConfig = {
  component: AsyncSelect,
  path: 'nationalitaet',
  label: 'Nationalität',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.NATIONALITY,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
};

export const berufsstatus: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.berufsstatus',
  label: 'Berufsstatus',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.KFZ_BERUFSSTATUS,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
};

export const beruf: FieldConfig = {
  component: AsyncSelect,
  path: 'beruf',
  label: 'Beruf',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.JOBS,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
  isRequired: true,
};

export const speziellerArbeitgeber: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.speziellerArbeitgeber',
  label: 'Spezieller Arbeitgeber',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.SPECIAL_EMPLOYER,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
};

export const tarifgruppe: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.tarifgruppe',
  label: 'Tarifgruppe',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.KFZ_TARIFGRUPPE,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
};

export const fuehrerscheindatum: FieldConfig = {
  component: Datepicker,
  path: 'fuehrerscheindatum',
  label: 'Führerschein erworben am',
  isRequired: true,
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
  infoIcon: {
    type: INFO_ICON_TYPE.POPOVER,
    content: computed(() => {
      const { sparte } = useGlobalStore();
      let minAge = 18;
      if (isKradSparte(sparte)) {
        minAge =
          useInputStore().allgemein.wkz !== WKZ.KRAFTRAEDER_KRAFTROLLER
            ? 16
            : 18;
      }

      return `Fahrer muss beim Führerscheinerwerb mindestens ${minAge} Jahre alt sein oder am begleiteten Fahren teilgenommen haben.`;
    }),
  },
  isDisabled: (context: RenderingContext<KfzFahrerResource>) => {
    if (context.client !== undefined) {
      const nutzerFuehrerscheindatum = context.client.fuehrerscheindatum;
      return (
        (nutzerFuehrerscheindatum === null ||
          nutzerFuehrerscheindatum === undefined) &&
        context.client.meta.isFullAge
      );
    }

    return true;
  },
  clientField: true,
};

export const vnIsFullAge: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.isFullAge',
  label: 'Bei Erwerb 18',
  inlineGroup: true,
  formGroupClass: ['justify-end', 'mb-2'],
  renderWhen: (context: RenderingContext<KfzFahrerResource>) => {
    if (context.client !== undefined) {
      return (
        context.client.fuehrerscheindatum === null ||
        context.client.fuehrerscheindatum === undefined
      );
    }

    return true;
  },
  clientField: true,
  revalidateClient: ['fuehrerscheindatum'],
};

export const hasMinderjaehrigeKinder: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.hasMinderjaehrigeKinder',
  label: 'Kinder unter 18 Jahren im Haushalt',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.MINOR_CHILDREN,
  },
  clientField: true,
  renderWhen: isEinzelnutzer,
};

export const minderjaehrigeKinderAlter: FieldConfig = {
  component: DsFormattedNumberInput,
  path: 'meta.minderjaehrigeKinder.alter',
  label: 'Alter minderjähriges Kind',
  inputAttrs: ageFormattedNumberInputAttrs,
  clientField: true,
  renderWhen: (context: RenderingContext<KfzFahrerResource>) => {
    if (context.client !== undefined) {
      return getSimpleChoiceMeta(
        SIMPLE_CHOICE.MINOR_CHILDREN,
        SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND,
      ).includes(context.client.meta.hasMinderjaehrigeKinder);
    }

    return true;
  },
};

export const hasMinderjaehrigeKinderAlterAeltestesKind: FieldConfig = {
  component: DsFormattedNumberInput,
  path: 'meta.minderjaehrigeKinder.alterAeltestesKind',
  label: 'Alter ältestes Kind',
  inputAttrs: ageFormattedNumberInputAttrs,
  clientField: true,
  renderWhen: hasEinzelnutzerMehrereKinder,
};

export const hasMinderjaehrigeKinderAlterJuengstesKind: FieldConfig = {
  component: DsFormattedNumberInput,
  path: 'meta.minderjaehrigeKinder.alterJuengstesKind',
  label: 'Alter jüngstes Kind',
  inputAttrs: ageFormattedNumberInputAttrs,
  clientField: true,
  renderWhen: hasEinzelnutzerMehrereKinder,
};

export const hasFuehrerscheinInEUErworben: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.hasFuehrerscheinInEUErworben',
  label: 'EU Führerschein',
  inlineGroup: true,
  clientField: true,
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
  renderWhen: isEinzelnutzer,
};

export const hasAnBegleitetenFahrenTeilgenommen: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.hasAnBegleitetenFahrenTeilgenommen',
  label: 'Begleitetes Fahren',
  inlineGroup: true,
  clientField: true,
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
  revalidateClient: ['fuehrerscheindatum'],
};

export const hasAnSicherheitstrainingTeilgenommen: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.hasAnSicherheitstrainingTeilgenommen',
  clientField: true,
  label: 'Teilnahme am Sicherheitstraining in den letzten 12 Monaten',
  inlineGroup: true,
  inputAttrs: {
    style: {
      'align-items': 'flex-start',
    },
  },
  renderWhen: isEinzelnutzerAndNotKfzV2Sparte,
};

export const hasPunkteInFlensburg: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.hasPunkteInFlensburg',
  label: 'Punkte in Flensburg',
  clientField: true,
  inlineGroup: true,
};

export const isMitarbeiterAutohersteller: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.isMitarbeiterAutohersteller',
  clientField: true,
  label: 'Firmenwagen aus eigener Produktion',
  inlineGroup: true,
  renderWhen: isEinzelnutzer,
};

export const isVorsteuerAbzugsberechtigt: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.isVorsteuerAbzugsberechtigt',
  clientField: true,
  label: 'Versicherungsnehmer vorsteuerabzugsberechtigt',
  inlineGroup: true,
  renderWhen: isEinzelnutzerAndVorsteuerabzugsBerechtigt,
};

export const isHauptnutzer: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.isHauptnutzer',
  label: 'Hauptnutzer',
  inlineGroup: true,
  clientField: true,
  renderWhen: computed(
    () => useInputStore().fahrerkreis.isVnHauptnutzer === false,
  ),
  isDisabled: (context: RenderingContext<KfzFahrerResource>) => {
    if (context.client) {
      return (
        !context.client.meta.isHauptnutzer &&
        getDriverWithHauptnutzerFlag() === 1
      );
    }

    return false;
  },
};

export const hasWohnsitzMin5JahreInBrd: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.hasWohnsitzMin5JahreInBrd',
  clientField: true,
  label: 'Wohnsitz seit mindestens 5 Jahren in Deutschland',
  inlineGroup: true,
  renderWhen: (context: RenderingContext<KfzFahrerResource>) => {
    if (context.client !== undefined) {
      return !getSimpleChoiceMeta(
        SIMPLE_CHOICE.NATIONALITY,
        SIMPLE_CHOICE_META.NATIONALITY_GERMAN,
      ).includes(context.client.nationalitaet);
    }

    return true;
  },
};

export const hasJahreskarteOeffentlicherPersonenverkehr: FieldConfig = {
  component: DsSwitch,
  path: 'meta.hasJahreskarteOeffentlicherPersonenverkehr',
  clientField: true,
  label: 'Jahreskarte ÖPNV',
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
  renderWhen: isEinzelnutzer,
};

export const jahreskarteOeffentlicherPersonenverkehrBahnNormal: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.jahreskarteOeffentlicherPersonenverkehr.bahnNormal',
  clientField: true,
  label: 'Jahreskarte Bahncard Normal',
  inlineGroup: true,
  renderWhen: hasEinzelnutzerJahreskarteOeffentlicherPersonenverkehr,
};

export const jahreskarteOeffentlicherPersonenverkehrBahnComfort: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.jahreskarteOeffentlicherPersonenverkehr.bahnComfort',
  clientField: true,
  label: 'Jahreskarte Bahncard Comfort',
  inlineGroup: true,
  renderWhen: hasEinzelnutzerJahreskarteOeffentlicherPersonenverkehr,
};

export const jahreskarteOeffentlicherPersonenverkehrSonstige: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.jahreskarteOeffentlicherPersonenverkehr.sonstige',
  clientField: true,
  label: 'Sonstige ÖPNV Jahreskarte',
  inlineGroup: true,
  renderWhen: hasEinzelnutzerJahreskarteOeffentlicherPersonenverkehr,
};

export const hasWohneigentum: FieldConfig = {
  component: DsSwitch,
  path: 'meta.hasWohneigentum',
  clientField: true,
  label: 'Besitz von Wohneigentum',
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
  renderWhen: isEinzelnutzer,
};

export const wohneigentumTyp: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.wohneigentum.typ',
  clientField: true,
  label: 'Typ des Wohneigentums',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.HOME_OWNERSHIP_TYPE,
  },
  renderWhen: hasEinzelnutzerWohneigentum,
};

export const wohneigentumGesellschaftID: FieldConfig = {
  component: AsyncSelect,
  path: 'meta.wohneigentum.gesellschaftID',
  clientField: true,
  label: 'Versichernde Gesellschaft des Wohneigentums',
  inputAttrs: {
    'choices-list': SIMPLE_CHOICE.GESELLSCHAFTEN_TS,
    numeric: true,
  },
  renderWhen: hasEinzelnutzerWohneigentum,
  isRequired: true,
};

export const wohneigentumIsVermietet: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.wohneigentum.isVermietet',
  clientField: true,
  label: 'Wohneigentum vermietet',
  inlineGroup: true,
  renderWhen: hasEinzelnutzerWohneigentum,
};

export const inHaeuslicherGemeinschaftMitVN: FieldConfig = {
  component: DsCheckbox,
  path: 'meta.inHaeuslicherGemeinschaftMitVN',
  label: 'Lebt mit Versicherungsnehmer in häuslicher Gemeinschaft',
  inlineGroup: true,
  inputAttrs: {
    style: {
      'align-items': 'flex-start',
    },
  },
  clientField: true,
  formGroupClass: [FORM_LAYOUT.START_NEXT_ROW_XL],
};

export const fahrerkreisTyp: FieldConfig = {
  component: FahrerkreisTypGroup,
  path: 'fahrerkreis.typ',
  label: 'Fahrerkreistyp',
};

export const isVnFahrer: FieldConfig = {
  component: DsCheckbox,
  path: 'fahrerkreis.isVnFahrer',
  label: 'Versicherungsnehmer ist Fahrer',
  inlineGroup: true,
  renderWhen: isNotCompanyAndOnlyVn,
};

export const isVnHauptnutzer: FieldConfig = {
  component: DsCheckbox,
  path: 'fahrerkreis.isVnHauptnutzer',
  label: 'Versicherungsnehmer ist Hauptnutzer',
  inlineGroup: true,
  renderWhen: computed(
    () => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT,
  ),
};

export const altersbeschraenkung: FieldConfig = {
  component: DsSwitch,
  path: 'fahrerkreis.altersbeschraenkung',
  label: 'Altersbeschränkung',
  renderWhen: computed(
    () => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.OFFEN,
  ),
};

export const alterJuengsterFahrer: FieldConfig = {
  component: DsFormattedNumberInput,
  path: 'fahrerkreis.alterJuengsterFahrer',
  label: 'Alter jüngster Fahrer',
  inputAttrs: ageFormattedNumberInputAttrs,
  renderWhen: hasAgeRestriction,
  isRequired: true,
};

export const alterAeltesterFahrer: FieldConfig = {
  component: DsFormattedNumberInput,
  path: 'fahrerkreis.alterAeltesterFahrer',
  label: 'Alter ältester Fahrer',
  inputAttrs: ageFormattedNumberInputAttrs,
  renderWhen: hasAgeRestriction,
  isRequired: true,
};

export const addDriver: FieldConfig = {
  component: DsButton,
  isDisabled: computed(() =>
    useClientStore().hasClientWithRole(OPTIONAL_ROLE.FAHRER_5),
  ),
  inputAttrs: {
    icon: 'plus',
  },
  handlers: {
    onClick: () => {
      if (useClientStore().hasClientWithRole(OPTIONAL_ROLE.FAHRER_5)) {
        return;
      }

      useClientStore().addNewClient(
        [nextAvailableFahrerRole.value as Role],
        false,
        kfzFahrerSchema.getDefault(),
      );
    },
  },
  slots: {
    default() {
      return 'Fahrer hinzufügen';
    },
  },
  renderWhen: computed(
    () => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT,
  ),
};
